import React, { useState } from "react";
import { toast } from "react-toastify";
import Search from "../../components/form/Search";
import Button from "../../components/form/Button";
import Table from "../../components/elements/table/Table";
import { setTitle } from "../../helpers/MetaTag";
import { handleFilterChange } from "../../helpers";
import DeleteModal from "../../components/common/DeleteModal";

import useFetch from "../../hooks/useFetch";
import { useDebounce } from "../../hooks/useDebounce";
import LimitSelect from "../../components/elements/LimitSelect";
import {
  testimonialsDelete,
  testimonialsList,
  testimonialsStatusChange,
} from "../../services/testimonialsService";
import useMutation from "../../hooks/useMutation";
import Switch from "../../components/form/Switch";
import Badge from "../../components/elements/Badge";
import ManageTestimonialsModal from "../../components/sections/admin/testimonials/modals/ManageTestimonialsModal";
import dayjs from "dayjs";
import useForm from "../../hooks/useForm";
import Input from "../../components/form/Input";
import { useGetPermission } from "../../hooks/useGetPermission";

const Testimonials = () => {
  setTitle("Vpeters Admin | testimonials");
  const { permission, status } = useGetPermission("testimonials");

  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data, error, isLoading, updateData, isFetching, refetch } = useFetch({
    dependencies: ["testimonials", filters, search, permission?.list],
    fetchFunction: async () => {
      const res = await testimonialsList({
        ...filters,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!permission?.list && status === "ok",
  });

  //   testimonials status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        const res = await testimonialsStatusChange({
          ids: id,
          status,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          updateData((pre) => {
            pre.data = (pre?.data || []).map((elm) =>
              elm?._id === payload?.id
                ? { ...elm, status: payload?.status === 1 ? 2 : 1 }
                : elm
            );
            return { ...pre };
          });
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const tableHeadData = [
    {
      _id: 1,
      name: "User Name",
      align: "left",
    },
    {
      _id: 2,
      name: "User Location",
      align: "left",
    },
    {
      _id: 3,
      name: "Testimony",
      align: "left",
    },
    {
      _id: 4,
      name: "Status",
      align: "left",
    },
    {
      _id: 5,
      name: "Date Submitted",
      align: "left",
    },
    ...(permission?.edit || permission?.delete
      ? [
          {
            _id: 6,
            name: "Action",
            align: "left",
          },
        ]
      : []),
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.name || "-",
          image: item?.image?.url,
          isTooltip: false,
        },
        {
          _id: 2,
          align: "left",
          type: "text",
          data: item?.address || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            (
              <div
                className="w-[300px] line-clamp-2"
                dangerouslySetInnerHTML={{ __html: item?.comments }}
              ></div>
            ) || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-1 items-center">
              <Badge
                type={item?.status === 1 ? "success" : "danger"}
                label={
                  item?.status === 1
                    ? "Published"
                    : item?.status === 2
                    ? "UnPublished"
                    : "Pending"
                }
              />
            </div>
          ),
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.dateOfSubmission
            ? dayjs(item?.dateOfSubmission).format("DD MMM, YYYY")
            : "-",
        },
        ...(permission?.edit || permission?.delete
          ? [
              {
                _id: 6,
                type: "action",
                align: "left",
                cellClass: "w-[5%]",
                actionData: [
                  ...(permission?.edit && status === "ok"
                    ? [
                        {
                          _id: 1,
                          name: "Edit",
                          icon: "fa-regular fa-pen-to-square",
                          onClick: () => {
                            setModalOpen({ isOpen: true, data: item });
                          },
                          standout: false,
                        },
                      ]
                    : []),
                  ...(permission?.delete && status === "ok"
                    ? [
                        {
                          _id: 2,
                          name: "Delete",
                          icon: "fa-regular fa-trash-can",
                          standout: true,
                          onClick: () => {
                            setDeleteModal({
                              isOpen: true,
                              data: item,
                            });
                          },
                        },
                      ]
                    : []),
                ],
              },
            ]
          : []),
      ],
    };
  });

  // add or edit testimonials =================>
  function testimonialsManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          updateData((pre) => ({
            ...pre,
            data: [res?.data, ...(pre?.data || [])],
          }));
          refetch();
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        updateData((pre) => ({
          ...pre,
          data: pre?.data?.map((item) =>
            item?._id === res?.data?._id ? res?.data : item
          ),
        }));
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   testimonials delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await testimonialsDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        updateData((pre) => {
          pre.data = (pre?.data || [])?.filter(
            (item) => !payload?.includes(item?._id)
          );
          return { ...pre };
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          refetch();
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-bold text-center text-lg xl:text-3xl text-chestnut-rose">
          Testimonials Management 
        </div>
        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="w-72">
            <Search
              placeholder={"Search here"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
          {permission?.add && status === "ok" && (
            <div className="relative">
              <Button
                buttonClasses={
                  "!bg-transparent border border-transparent !h-10 hover:!bg-chestnut-rose/10 hover:!border-chestnut-rose/10"
                }
                buttonLabel={"Create New"}
                buttonLabelClasses={"font-medium !text-sm text-chestnut-rose"}
                buttonFunction={() =>
                  setModalOpen({ isOpen: true, data: null })
                }
              />
            </div>
          )}
        </div>
      </div>
      {status === "ok" &&
        (permission?.list ? (
          <Table
            tableHeadData={tableHeadData}
            tableData={tableData}
            isLoder={isLoading}
            pageCount={data?.metadata?.totalPages}
            currentPage={filters?.page}
            onPageChange={(val) => {
              handleFilterChange("page", val, setFilters);
              setSelectedItems([]);
            }}
            handleOrdering={(val) => {
              console.log(val);
            }}
            isFetching={isFetching}
            isOrderingDisabled={true}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            tableActionDropdownContainer={"!w-48"}
          />
        ) : (
          <div className="font-semibold text-xl text-center h-60 flex items-center justify-center text-shades-07">
            You don't have permission to view this list.
          </div>
        ))}
      <ManageTestimonialsModal
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={testimonialsManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Testimonials;
